import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader, { StyledModalHeader } from 'LuxKit/Modal/ModalHeader'
import TripListMoveDayMenuItem from './TripListMoveDayMenuItem'
import type { TripListMoveDayOption } from './tripListMoveDayOptions'

const SHEET_TOP_RADIUS = rem(32)

const MoveDaySheetModal = styled(ModalBase)`
  ${mediaQueryOnly.mobile} {
    &&&.mode-default.height-auto {
      border-start-start-radius: ${SHEET_TOP_RADIUS};
      border-start-end-radius: ${SHEET_TOP_RADIUS};
      border-end-end-radius: 0;
      border-end-start-radius: 0;
      overflow: hidden;
    }

    &&&.mode-default.height-auto ${StyledModalHeader} {
      border-start-start-radius: ${SHEET_TOP_RADIUS};
      border-start-end-radius: ${SHEET_TOP_RADIUS};
      border-end-end-radius: 0;
      border-end-start-radius: 0;
      padding-block-start: ${rem(20)};
    }
  }

  ${mediaQueryUp.tablet} {
    &&&.mode-default.height-auto {
      border-radius: ${props => props.theme.borderRadius.L};
    }
  }

  &,
  > form {
    grid-template-areas:
      "modal-header"
      "modal-body";
    grid-template-rows:
      min-content
      1fr;
  }
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  padding-block-end: env(safe-area-inset-bottom);
`

interface Props {
  options: Array<TripListMoveDayOption>
}

/**
 * Mobile bottom sheet for picking a destination day when moving list items.
 * Resolves the chosen option (or undefined on dismiss). Desktop selection Move
 * keeps using DropdownList — this sheet is only opened from the mobile toolbar.
 */
function TripListMoveDaySheet({ options }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<TripListMoveDayOption | undefined>()

  const title = intl.formatMessage({
    id: 'HapGLjj4DK7cTzBFH3yJ',
    defaultMessage: 'Move to another day',
    description: 'Dropdown menu item for opening day move options in the trip planner list row',
  })

  const handleClose = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const handleOptionClick = useCallback((option: TripListMoveDayOption) => {
    if (option.selected) return
    modalElement.resolve(option)
  }, [modalElement])

  return (
    <MoveDaySheetModal dismissible height="auto" size="S">
      <ModalHeader
        title={title}
        onCloseButtonClick={handleClose}
      />
      <ModalBody>
        <ModalContent flushListItems>
          <OptionList>
            {options.map(option => (
              <TripListMoveDayMenuItem
                key={option.id}
                option={option}
                onClick={handleOptionClick}
              />
            ))}
          </OptionList>
        </ModalContent>
      </ModalBody>
    </MoveDaySheetModal>
  )
}

export default TripListMoveDaySheet
